'use client';

import {getTranslation} from '@/lib/utils';
import {NewPageSection} from '@/types/TermPage';
import {HTMLAttributes, useEffect, useState} from 'react';
import {Locale} from '../../../../i18n.config';
import parser from 'html-react-parser';
import {parseOptions} from '@/utils/parseOptions';
import {useFAQ} from '@/hooks/useFAQContext';
import Link from 'next/link';
import {IFaqDictionary} from '@/dictionaries/pages/faq/types';

interface IFAQContentProps extends HTMLAttributes<HTMLDivElement> {
  sections: NewPageSection[];
  lang: Locale;
  dict: IFaqDictionary;
}

export const FAQContent: React.FC<IFAQContentProps> = ({
  sections,
  lang,
  dict,
}) => {
  const {changeHash, hash} = useFAQ();
  const [section, setSection] = useState(-1);
  const [subSection, setSubSection] = useState(-1);

  const sectionsList = sections;

  const suggestedTopics = sectionsList.slice(0, 3).map((section) => ({
    ...section,
    subSections: section.subsections.slice(0, 3),
  }));

  useEffect(() => {
    const [, sectionIndex, subSectionIndex] = hash.split('-');
    setSection(Number(sectionIndex) - 1);
    setSubSection(Number(subSectionIndex) - 1);
  }, [hash]);

  return (
    <section className="flex flex-col space-y-12 p-4 text-text">
      {hash &&
      sectionsList[section] &&
      sectionsList[section].subsections[subSection] ? (
        <>
          <h1 className="text-4xl text-gray-900 font-semibold tracking-widest">
            {
              getTranslation(sectionsList[section].pageSectionTranslation, lang)
                ?.title
            }
          </h1>

          <div className="space-y-4 leading-[1.25]">
            <h2 className="text-subtitle text-gray-900 leading-[1.6] font-semibold">
              {
                getTranslation(
                  sectionsList[section].subsections[subSection]
                    .pageSectionTranslation,
                  lang,
                )?.title
              }
            </h2>
            {parser(
              getTranslation(
                sectionsList[section].subsections[subSection]
                  .pageSectionTranslation,
                lang,
              )?.description || '',
              parseOptions,
            )}
          </div>
        </>
      ) : (
        <div className="flex flex-col items-center">
          <h1 className="text-4xl mb-20 text-gray-900 font-semibold">
            {dict.selectATopic}
          </h1>
          <h2 className="text-medium mb-12 font-title text-gray-900 font-semibold">
            {dict.suggestedTopic}
          </h2>
          <div className="flex flex-row w-full">
            {suggestedTopics.map((topic, topicIndex) => (
              <div key={topic.id} className="w-1/3 px-[15px] space-y-4">
                <h5 className="text-lg text-gray-900 leading-[1.6] font-semibold">
                  {getTranslation(topic.pageSectionTranslation, lang)?.title}
                </h5>
                <ul className="space-y-2">
                  {topic.subSections.map((subtopic, subtopicIndex) => {
                    const hashCode = `#faq-${topicIndex + 1}-${
                      subtopicIndex + 1
                    }`;

                    return (
                      <li key={subtopic.id}>
                        <Link
                          scroll={false}
                          shallow
                          onClick={(e) => {
                            e.preventDefault();
                            changeHash(hashCode);
                          }}
                          href={hashCode}>
                          {
                            getTranslation(
                              subtopic.pageSectionTranslation,
                              lang,
                            )?.title
                          }
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};
